// WindowedDocumentViewer.tsx
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Page, pdfjs, Document } from 'react-pdf';
import { memo, useRef, useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';

import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import { alpha } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';

import { useTranslate } from 'src/locales';

// The worker is configured once, for every PDF surface — see the module for why.
import 'src/components/documents/pdf-worker';
import { highlightToHtml } from 'src/components/highlight-text/highlight-text';

interface Props {
  fileUrl: string;
  /**
   * The document's real filename, when the URL cannot supply one.
   *
   * The renderer is chosen by file extension, and `fileUrl` is normally a storage URL that ends in
   * one. A guest document is not: it is fetched as a blob behind the guest session and wrapped in an
   * object URL, which ends in a UUID. Every PDF therefore matched no case, fell to the Office
   * viewer, and rendered as a broken frame — the report that started JT1-1491's third round.
   */
  fileName?: string;
  /**
   * The document's bytes, when the caller already holds them.
   *
   * pdf.js fetches whatever URL it is given, and `connect-src` in the CloudFront CSP does not list
   * the blob scheme — `*` matches only network schemes. So a guest's object URL was refused at the
   * fetch, even once the right renderer was chosen. Given a Blob, pdf.js reads it directly and no
   * request is made (JT1-1491).
   */
  fileBlob?: Blob;
  /**
   * Search terms to mark on a PDF's pages. Only a PDF can show them: an Office document renders in
   * Microsoft's cross-origin viewer, which this page cannot reach into. Pass a stable array — a new
   * one each render re-renders every page.
   */
  highlightTerms?: string[];
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

interface DocumentDimensions {
  width: string;
  height: string;
  minWidth?: string;
}

function getDocumentDimensions(fileType: string): DocumentDimensions {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxHeight = Math.min(viewportHeight * 0.9, 900); // 90vh or 900px, whichever is smaller

  switch (fileType) {
    case 'pdf':
      return {
        width: '800px',
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    case 'doc':
    case 'docx':
      return {
        width: `${Math.min(viewportWidth * 0.75, 1200)}px`,
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    case 'xls':
    case 'xlsx':
      return {
        width: '90vw',
        height: `${maxHeight}px`,
        minWidth: '1000px'
      };
    case 'ppt':
    case 'pptx':
      return {
        width: `${Math.min(viewportWidth * 0.8, 1200)}px`,
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    default:
      return {
        width: '800px',
        height: `auto`,
        minWidth: '800px'
      };
  }
}

const PDFViewer = memo(({ url, blob, width, highlightTerms, onError }: { url: string; blob?: Blob; width: number; highlightTerms?: string[]; onError: () => void }) => {
  const { t } = useTranslate();
  const [pageCount, setPageCount] = useState<number | null>(null);
  const passwordAttempts = useRef(0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPageCount(numPages);
  };

  // memoize the file object (prevent re-rendering on nav)
  // The Blob wins where the caller has it: pdf.js then reads the bytes instead of fetching a URL,
  // which is what a blob: URL cannot survive under this CSP.
  const file = useMemo(() => blob ?? { url }, [blob, url]);

  // Marks search hits in each page's text layer — the transparent, selectable copy of the text pdf.js
  // lays over the drawn page — so a search can keep the resume's formatting. Left off when there is
  // nothing to mark, so the layer renders exactly as pdf.js writes it.
  const customTextRenderer = useMemo(
    () => (highlightTerms?.length ? ({ str }: { str: string }) => highlightToHtml(str, highlightTerms) : undefined),
    [highlightTerms]
  );

  const handlePassword = useCallback((callback: Function, reason?: number) => {
    let newPassword = '';

    if (passwordAttempts.current >= 3) {
      alert(t('documents.document_viewer.password_error'));
      onError();
      return;
    }

    passwordAttempts.current += 1;

    switch (reason) {
      case 1: {
        newPassword = prompt(t('documents.document_viewer.password_prompt')) || '';

        callback(newPassword);
        break;
      }
      default:
        newPassword = prompt(t('documents.document_viewer.incorrect_password_prompt')) || '';
        callback(newPassword);
        break;
    }
  }, [passwordAttempts, t, onError]);

  return (
    <Document
      className="document-viewer-document"
      onPassword={handlePassword}
      loading={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      }
      error={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          {t('documents.document_viewer.load_error')}
        </Box>
      }
      file={file}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onError}
    >
      <Stack direction="column" alignItems="center" justifyContent="center">
        {Array.from(new Array(pageCount || 0), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            scale={1}
            width={width}
            customTextRenderer={customTextRenderer}
            loading={
              <Box sx={{
                height: 800,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <CircularProgress size={24} />
              </Box>
            }
          />
        ))}
      </Stack>
    </Document>
  );
});

const OfficeViewer = memo(({ url, dimensions }: { url: string; dimensions: DocumentDimensions }) => {
  const encodedUrl = encodeURIComponent(url);
  const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;

  return (
    <Box sx={{ height: '100%', overflowY: 'hidden' }}>
      <iframe
        src={viewerUrl}
        style={{
          border: 'none',
          width: '100%',
          height: '100%'
        }}
        title="document-viewer"
      />
    </Box>
  );
});

export default function DocumentViewer({ fileUrl, fileName, fileBlob, highlightTerms }: Props) {
  const documentRef = useRef<HTMLDivElement>(null);

  const [viewerError, setViewerError] = useState(false);

  // Fit the PDF page to the container width (capped at the 800px design width) so it never overflows a
  // narrow surface. The record-panel quick-view drawer (~640px) was clipping a fixed 800px page on the
  // left with no horizontal scroll (JT1-1537 QA). Re-measures on container/viewport resize.
  const [pdfWidth, setPdfWidth] = useState<number>(800);
  useLayoutEffect(() => {
    const el = documentRef.current;
    if (!el) return undefined;
    const measure = () => {
      const available = el.clientWidth - 32; // minus the container padding (p:2 => 16px each side)
      setPdfWidth(Math.max(Math.min(available, 800), 240));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The filename wins where one is given; `fileUrl` is the fallback for every existing caller,
  // which passes a storage URL that carries the extension.
  const fileType = useMemo(() =>
    getFileExtension(fileName || fileUrl || ''),
    [fileName, fileUrl]
  );

  const dimensions = useMemo(() =>
    getDocumentDimensions(fileType),
    [fileType]
  );

  useEffect(() => {
    const scrollKey = `doc-scroll-${fileUrl}`;
    return () => {
      if (documentRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        sessionStorage.setItem(scrollKey, documentRef.current.scrollTop.toString());
      }
    };
  }, [fileUrl]);

  useEffect(() => {
    const scrollKey = `doc-scroll-${fileUrl}`;
    const savedScrollPosition = sessionStorage.getItem(scrollKey);
    if (savedScrollPosition && documentRef.current) {
      documentRef.current.scrollTop = parseInt(savedScrollPosition, 10);
    }
  }, [fileUrl]);

  const documentViewer = useMemo(() => {
    if (!fileUrl) return null;

    if (viewerError) {
      return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
    }

    switch (fileType) {
      case 'pdf':
        return <PDFViewer url={fileUrl} blob={fileBlob} width={pdfWidth} highlightTerms={highlightTerms} onError={() => setViewerError(true)} />;
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
      case 'ppt':
      case 'pptx':
        return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tiff':
      case 'svg':
        return <img src={fileUrl} alt="document-preview" style={{ maxWidth: '100%', height: 'auto' }} />;
      default:
        return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
    }
  }, [fileUrl, fileBlob, viewerError, dimensions, fileType, pdfWidth, highlightTerms]);

  return (
    <Box
      ref={documentRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: 2,
        '& .react-pdf__Document': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        },
        // Search hits in the PDF text layer. That layer's text is transparent over the drawn page, so
        // the mark stays transparent too and only tints behind the real glyphs — the same tint the
        // parsed-text view uses.
        '& .react-pdf__Page__textContent mark': {
          color: 'transparent',
          borderRadius: 0.5,
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.4),
        }
      }}
    >
      {documentViewer}
    </Box>
  );
}
